<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from '../LaisCard.vue';
import ShareListingButton from '../ShareListingButton.vue';
import ClassifiedPriceBox from './ClassifiedPriceBox.vue';

const props = defineProps({
  card: { type: Object, required: true },
  active: { type: Boolean, default: false },
  showSelect: { type: Boolean, default: true },
  compact: { type: Boolean, default: false },
});

const emit = defineEmits([
  'open',
  'hover',
  'toggle-select',
  'public-link',
  'share',
  'share-error',
  'viewed',
]);

const { t } = useI18n();
const cardRoot = ref(null);
const hasEmittedView = ref(false);
let viewObserver = null;

const primaryCtaLabel = computed(
  () => props.card?.cta?.primary?.label || t('PROPERTIES.CARD.DETAILS')
);
const publicLinkLabel = computed(
  () => props.card?.cta?.secondary?.label || t('PROPERTIES.CARD.OPEN_PUBLIC')
);
const safeTags = computed(() =>
  Array.isArray(props.card?.tags) ? props.card.tags : []
);
const priceLabel = computed(
  () => props.card?.price?.label || t('PROPERTIES.CARD.PRICE')
);
const priceFormatted = computed(
  () => props.card?.price?.formatted || t('PROPERTIES.CARD.PRICE_ON_REQUEST')
);
const monthlySuffix = computed(() => props.card?.price?.monthlySuffix || '');
const isClassifiedCard = computed(() => {
  const attrs =
    props.card?.additionalAttributes ||
    props.card?.additional_attributes ||
    props.card?.raw?.additionalAttributes ||
    props.card?.raw?.additional_attributes ||
    {};
  const publicationType =
    props.card?.publicationType ||
    props.card?.publication_type ||
    props.card?.raw?.publicationType ||
    props.card?.raw?.publication_type ||
    attrs.publication_type ||
    attrs.publicationType;
  return attrs.classified === true || publicationType === 'classified';
});

const emitViewed = () => {
  if (hasEmittedView.value) return;
  hasEmittedView.value = true;
  emit('viewed', props.card);
};

onMounted(() => {
  const element = cardRoot.value?.$el || cardRoot.value;
  if (
    !element ||
    typeof window === 'undefined' ||
    !('IntersectionObserver' in window)
  ) {
    emitViewed();
    return;
  }

  viewObserver = new window.IntersectionObserver(
    entries => {
      if (!entries.some(entry => entry.isIntersecting)) return;
      emitViewed();
      viewObserver?.disconnect();
    },
    { threshold: 0.35 }
  );
  viewObserver.observe(element);
});

onBeforeUnmount(() => {
  viewObserver?.disconnect();
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisCard
    ref="cardRoot"
    density="flush"
    :active="active"
    class="group h-full"
    @mouseenter="emit('hover', card)"
  >
    <div class="grid h-full grid-rows-[auto_1fr_auto]">
      <div
        class="relative overflow-hidden bg-lais-bg-muted"
        :class="compact ? 'h-28' : 'h-40'"
      >
        <img
          v-if="card.coverPhoto"
          :src="card.coverPhoto"
          :alt="card.photoAlt || t('PROPERTIES.CARD.IMAGE_ALT_FALLBACK')"
          class="size-full object-cover transition duration-300 group-hover:scale-[1.02]"
          :class="card.unavailable ? 'opacity-70 grayscale' : ''"
          :loading="card.position === 1 ? 'eager' : 'lazy'"
          :fetchpriority="card.position === 1 ? 'high' : 'auto'"
        />
        <div
          v-else
          class="flex size-full flex-col items-center justify-center gap-2 text-lais-text-muted"
        >
          <span class="i-lucide-image-off size-8" aria-hidden="true" />
          <span class="text-[12px] font-semibold">
            {{ t('PROPERTIES.CARD.NO_PHOTO') }}
          </span>
        </div>
        <div
          class="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/45 to-transparent"
          aria-hidden="true"
        />

        <div class="absolute left-3 top-3 flex flex-wrap gap-1.5">
          <span
            class="rounded-full px-2 py-1 text-[11px] font-semibold uppercase tracking-wide"
            :class="card.statusClass"
          >
            {{ card.statusLabel }}
          </span>
          <span
            v-if="card.unavailable"
            class="rounded-full bg-white/90 px-2 py-1 text-[11px] font-semibold text-rose-700"
          >
            {{ t('PROPERTIES.CARD.UNAVAILABLE') }}
          </span>
        </div>

        <label
          v-if="showSelect"
          class="absolute right-3 top-3 grid size-8 cursor-pointer place-items-center rounded-full bg-white/95 shadow-lais-card"
          :aria-label="
            card.selected
              ? t('PROPERTIES.CARD.REMOVE_SELECTION')
              : t('PROPERTIES.CARD.SELECT_PROPERTY')
          "
          @click.stop
        >
          <input
            type="checkbox"
            class="size-4 accent-lais-primary"
            :checked="card.selected"
            @change="emit('toggle-select', card)"
          />
        </label>
      </div>

      <div class="grid gap-3 p-4">
        <div class="min-w-0">
          <button
            type="button"
            class="block w-full text-left"
            @click="emit('open', card)"
          >
            <h3
              class="line-clamp-2 text-[15px] font-semibold leading-snug text-lais-text"
            >
              {{ card.title }}
            </h3>
            <p
              v-if="card.code"
              class="mt-1 text-[11px] font-semibold text-lais-primary"
            >
              {{ t('PROPERTIES.CARD.CODE', { code: card.code }) }}
            </p>
          </button>
          <p class="mt-1 text-[12px] text-lais-text-muted">
            {{ card.typeLabel }} - {{ card.businessContextLabel }}
          </p>
          <p v-if="card.details" class="mt-1 text-[12px] text-lais-text-helper">
            {{ card.details }}
          </p>
        </div>

        <div class="grid gap-1 text-[12px] text-lais-text-muted">
          <p class="line-clamp-2">
            <strong class="text-lais-text">
              {{ t('PROPERTIES.CARD.LOCATION') }}:
            </strong>
            <span class="ml-1">{{ card.location }}</span>
          </p>
          <ClassifiedPriceBox
            v-if="isClassifiedCard"
            :price="card.price"
            :business-context="card.businessContext"
          />
          <p v-else>
            <strong class="text-lais-text">{{ priceLabel }}:</strong>
            <span class="ml-1 font-semibold text-lais-text">
              {{ priceFormatted }}
            </span>
            <span v-if="monthlySuffix" class="ml-1">
              {{ monthlySuffix }}
            </span>
          </p>
        </div>

        <div class="flex flex-wrap gap-1.5">
          <span
            v-if="card.inventoryLabel"
            class="rounded-full bg-lais-primary-50 px-2 py-1 text-[11px] font-semibold text-lais-primary"
          >
            {{ card.inventoryLabel }}
          </span>
          <span
            v-for="tag in safeTags.slice(0, 5)"
            :key="`${card.id}-${tag.key}`"
            class="rounded-full px-2 py-1 text-[11px] font-semibold"
            :class="
              tag.kind === 'flag'
                ? 'bg-amber-50 text-amber-800'
                : 'bg-lais-bg-soft text-lais-text-body'
            "
          >
            {{ tag.label }}
          </span>
        </div>
      </div>

      <footer
        class="flex flex-col gap-2 border-t border-lais-border-soft px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
      >
        <button
          type="button"
          class="inline-flex h-9 w-full items-center justify-center gap-2 rounded-lais bg-lais-primary px-3 text-[13px] font-semibold text-white transition-colors hover:bg-lais-primary-600 sm:w-auto"
          @click="emit('open', card)"
        >
          <span class="i-lucide-eye size-4" aria-hidden="true" />
          <span class="max-w-[8rem] truncate">{{ primaryCtaLabel }}</span>
        </button>
        <div class="flex items-center gap-2">
          <ShareListingButton
            :url="card.publicUrl"
            :title="card.title"
            source="dashboard_card"
            @shared="payload => emit('share', card, payload)"
            @error="error => emit('share-error', card, error)"
          />
          <button
            type="button"
            class="grid size-9 place-items-center rounded-lais border border-lais-border bg-white text-lais-primary transition-colors hover:border-lais-primary hover:bg-lais-primary-50 disabled:cursor-not-allowed disabled:opacity-40"
            :disabled="!card.publicUrl"
            :aria-label="publicLinkLabel"
            @click.stop="emit('public-link', card)"
          >
            <span class="i-lucide-globe-2 size-4" aria-hidden="true" />
          </button>
        </div>
      </footer>
    </div>
  </LaisCard>
</template>
